import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { 
  Plus, 
  CheckCircle, 
  AlertCircle,
  Clock,
  User,
  Circle,
  AlertTriangle,
  Archive,
  MessageSquare,
  GitPullRequest,
  GitPullRequestClosed,
  Filter,
  X,
  Bot
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";

import { Investigation } from "../../types/investigation";
import { listInvestigations } from "../../clients/metoro/investigations";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { withErrorHandling } from "../../utility/apiUtils";
import CreateInvestigationDialog from "./CreateInvestigationDialog";
import { cn } from "../ui/lib/utils";
import { DataTable } from "../ui/datatable/datatable";
import { DataTableColumnHeader } from "../ui/datatable/datatablecolumnheader";

// Priority configuration
const PRIORITY_CONFIG = {
  P1: { label: "Critical", color: "bg-red-500", textColor: "text-red-500", icon: AlertTriangle },
  P2: { label: "High", color: "bg-orange-500", textColor: "text-orange-500", icon: AlertCircle },
  P3: { label: "Medium", color: "bg-yellow-500", textColor: "text-yellow-500", icon: AlertCircle },
  P4: { label: "Low", color: "bg-blue-500", textColor: "text-blue-500", icon: Circle },
  P5: { label: "Info", color: "bg-gray-500", textColor: "text-gray-500", icon: Circle }
};

// Status configuration
const STATUS_CONFIG = {
  active: { label: "Open", color: "text-green-600", bgColor: "bg-green-100", icon: GitPullRequest },
  in_progress: { label: "In Progress", color: "text-yellow-600", bgColor: "bg-yellow-100", icon: Clock },
  resolved: { label: "Closed", color: "text-purple-600", bgColor: "bg-purple-100", icon: GitPullRequestClosed },
  archived: { label: "Archived", color: "text-gray-600", bgColor: "bg-gray-100", icon: Archive }
};

const InvestigationsTable: React.FC = () => {
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["investigations"],
    queryFn: () =>
      withErrorHandling(() =>
        listInvestigations({
          limit: 1000,
          offset: 0,
          includeResolved: true,
        })
      ),
  });

  interface InvestigationTableRow extends Investigation {
    status: string;
    priority: string;
    assignee: string;
  }

  const investigations = useMemo(() => {
    // Transform investigations to add computed fields for filtering
    return (data?.investigations || []).map(inv => ({
      ...inv,
      status: inv.resolvedAt ? "resolved" : "active",
      priority: inv.tags?.priority || "P3",
      creator: inv.creator || "unknown",
      assignee: inv.tags?.assignee || "Unassigned",
    } as InvestigationTableRow));
  }, [data]);

  const handleRowClick = useCallback(
    (investigation: Investigation) => {
      navigate(`/investigation?uuid=${investigation.uuid}`);
    },
    [navigate]
  );

  // Define columns for DataTable
  const columns: ColumnDef<InvestigationTableRow>[] = [
    {
      id: "title",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Investigation" className="w-full" />
      ),
      accessorKey: "title",
      cell: ({ row }) => {
        return (
          <div 
            className="flex flex-col group w-full"
          >
            <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-medium text-textlight group-hover:text-primary transition-colors">
                  {row.original.title}
                </span>
                {row.original.chatHistoryUuid && (
                  <Badge
                    variant="secondary"
                    className="gap-1 bg-purple-600/20 text-purple-400 border-purple-600/40 border h-5 px-1.5"
                  >
                    <Bot className="h-3 w-3" />
                    <span className="text-xs">AI</span>
                  </Badge>
                )}
                {row.original.tags && Object.entries(row.original.tags)
                  .filter(([key]) => key !== "priority" && key !== "assignee")
                  .slice(0, 3)
                  .map(([key, value]) => (
                    <span
                      key={key} 
                      className="inline-flex items-center text-xs rounded overflow-hidden border border-border"
                    >
                      <span className="px-1.5 py-0.5 bg-backgrounddark text-textmedium">
                        {key}
                      </span>
                      <span className="px-1.5 py-0.5 bg-backgroundlight text-textlight">
                        {String(value)}
                      </span>
                    </span>
                  ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-textdark">
                <span>#{row.original.uuid.slice(0, 8)}</span>
                <span>
                  opened {format(new Date(row.original.createdAt), "MMM d")} by {row.original.creator}
                </span>
                {row.original.resolvedAt && (
                  <>
                    <span>•</span>
                    <span>closed {format(new Date(row.original.resolvedAt), "MMM d")}</span>
                  </>
                )}
              </div>
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        const statusConfig = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
        const StatusIcon = statusConfig.icon;
        
        return (
          <div className="flex items-center">
            <span className={cn(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
              status === "active" 
                ? "bg-green-100 text-green-700" 
                : "bg-red-100 text-red-700"
            )}>
              <StatusIcon className="h-3 w-3" />
              {status === "active" ? "Open" : "Closed"}
            </span>
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
      enableColumnFilter: true,
    },
    {
      id: "priority",
      accessorKey: "priority",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Priority" />
      ),
      cell: ({ row }) => {
        const priority = row.getValue("priority") as string;
        const priorityConfig = PRIORITY_CONFIG[priority as keyof typeof PRIORITY_CONFIG];
        const PriorityIcon = priorityConfig?.icon || Circle;
        
        return (
          <div className="flex items-center gap-1">
            <PriorityIcon className={cn("h-3.5 w-3.5", priorityConfig?.textColor || "text-gray-500")} />
            <span className={cn(
              "text-sm font-medium",
              priorityConfig?.textColor || "text-gray-500"
            )}>
              {priority}
            </span>
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
      enableColumnFilter: true,
    },
    {
      id: "assignee",
      accessorKey: "assignee",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Assignee" />
      ),
      cell: ({ row }) => {
        const assignee = row.getValue("assignee") as string;
        return (
          <div className="flex items-center gap-1">
            <User className="h-3 w-3 text-textdark" />
            <span className="text-sm text-textmedium">{assignee}</span>
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
      enableColumnFilter: true,
    },
    {
      id: "creator",
      accessorKey: "creator",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Author" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <User className="h-3 w-3 text-textdark" />
          <span className="text-sm text-textmedium">{row.getValue("creator")}</span>
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
      enableColumnFilter: true,
      // @ts-ignore
      show: false,
    },
    {
      id: "updatedAt",
      accessorKey: "updatedAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Updated" />
      ),
      cell: ({ row }) => {
        const updatedAt = row.getValue("updatedAt") as string;
        const date = new Date(updatedAt);
        const now = new Date();
        const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
        
        let timeString;
        if (diffInHours < 1) {
          const diffInMinutes = Math.floor(diffInHours * 60);
          timeString = `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
          timeString = `${Math.floor(diffInHours)}h ago`;
        } else if (diffInHours < 24 * 7) {
          const diffInDays = Math.floor(diffInHours / 24);
          timeString = `${diffInDays}d ago`;
        } else {
          timeString = format(date, "MMM d");
        }
        
        return (
          <span className="text-sm text-textdark">
            {timeString}
          </span>
        );
      },
      sortingFn: "datetime",
      enableColumnFilter: false,
    },
  ];

  const filters: { filteringColumn: string; title: string; defaultValues?: string[] }[] = [
    {
      filteringColumn: "status",
      title: "Status",
      defaultValues: ["active"],
    },
    {
      filteringColumn: "priority",
      title: "Priority",
    },
    {
      filteringColumn: "assignee",
      title: "Assignee",
    },
    {
      filteringColumn: "creator",
      title: "Author",
    },
  ];

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load investigations</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex-1 overflow-hidden p-2">
        <div className="h-full border border-border rounded-lg overflow-hidden flex flex-col">
          <div className="border-b border-border bg-backgroundlight px-4 py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-8 px-3 text-textmedium hover:text-textlight">
                <GitPullRequest className="h-4 w-4 mr-2" />
                <span className="font-medium">{investigations.filter(inv => inv.status === "active").length} Open</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-3 text-textmedium hover:text-textlight">
                <CheckCircle className="h-4 w-4 mr-2" />
                <span className="font-medium">{investigations.filter(inv => inv.status === "resolved").length} Closed</span>
              </Button>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-green-600 hover:bg-green-700 text-white border-0"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              New investigation
            </Button>
          </div>
          <DataTable
            className="flex-1 border-0"
            removeBorder={true}
            columns={columns}
            data={investigations}
            textFilteringColumnName="title"
            textFilteringPlaceholder="Search all investigations"
            filters={filters}
            showPagination={true}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            emptyStateMessage={
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-backgrounddark mb-4">
                  <GitPullRequest className="h-8 w-8 text-textdark" />
                </div>
                <h3 className="text-lg font-medium text-textlight mb-1">No investigations yet</h3>
                <p className="text-textdark mb-6">Get started by creating a new investigation.</p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-green-600 hover:bg-green-700 text-white border-0"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New investigation
                </Button>
              </div>
            }
            defaultPageSize={30}
          />
        </div>
      </div>

      {showCreateDialog && (
        <CreateInvestigationDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onSuccess={() => {
            setShowCreateDialog(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default InvestigationsTable;